import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Volume2, VolumeX } from "lucide-react";
import { dictionary } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Marquee } from "@/components/motion";
import { createWaveAmbience } from "@/introSound";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import Licensing from "@/pages/Licensing";
import Impact from "@/pages/Impact";
import Pseq2 from "@/pages/Pseq2";
import Admin from "@/pages/Admin";
import Oracle from "@/pages/Oracle";
import Contact from "@/pages/Contact";

const API = process.env.REACT_APP_BACKEND_URL;

const LangContext = createContext(null);
export const useLang = () => useContext(LangContext);

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const SUPPORTED_LANGS = ["es", "en", "pt", "fr", "ru", "ar", "zh", "ja"];

function detectLang() {
  const langs = navigator.languages || [navigator.language || "es"];
  for (const l of langs) {
    const code = String(l).slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return "es";
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        const timer = setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
        return () => clearTimeout(timer);
      }
    }
    window.lenis?.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
}

function GlobalAmbience() {
  const [muted, setMuted] = useState(false);
  const ambRef = useRef(null);

  useEffect(() => {
    const amb = createWaveAmbience();
    ambRef.current = amb;
    amb.enter();
    return () => amb.leave();
  }, []);

  useEffect(() => {
    ambRef.current?.setMuted(muted);
  }, [muted]);

  return (
    <button
      data-testid="global-sound-toggle"
      onClick={() => setMuted((m) => !m)}
      className="fixed bottom-5 left-5 z-[60] inline-flex items-center gap-1.5 rounded-full border border-deep/15 bg-white/80 p-2.5 text-deep/70 shadow-lg shadow-deep/10 backdrop-blur-md transition-colors duration-300 hover:border-teal hover:text-teal"
      aria-label={muted ? "Sound off" : "Sound on"}
    >
      {muted ? (
        <VolumeX className="h-4 w-4" />
      ) : (
        <>
          <Volume2 className="h-4 w-4" />
          <span className="eq" aria-hidden="true" data-testid="sound-equalizer">
            <span />
            <span />
            <span />
            <span />
          </span>
        </>
      )}
    </button>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: if returning from OAuth callback, skip the /me check — AuthCallback exchanges the session_id first
    if (window.location.hash?.includes("session_id=")) {
      setChecked(true);
      return;
    }
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
      setUser(r.ok ? await r.json() : null);
    } catch {
      setUser(null);
    }
    setChecked(true);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, checked, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthCallback() {
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = location.hash.split("session_id=")[1]?.split("&")[0];
    fetch(`${API}/api/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sid }),
    })
      .catch(() => {})
      .finally(() => window.location.replace("/"));
  }, [location.hash]);

  return <div className="min-h-screen bg-deep" data-testid="auth-callback" />;
}

function AppRouter() {
  const location = useLocation();
  // session_id arrives in the URL fragment after Google auth — process it before any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/licensing" element={<Licensing />} />
      <Route path="/impact" element={<Impact />} />
      <Route path="/pseq2" element={<Pseq2 />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/oracle" element={<Oracle />} />
      <Route path="/contact" element={<Contact />} />
    </Routes>
  );
}

function App() {
  const [baseLang] = useState(detectLang);
  const [lang, setLang] = useState(baseLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  const t = dictionary[lang];

  return (
    <LangContext.Provider value={{ lang, setLang, baseLang, t }}>
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <div className="min-h-screen bg-bone font-body text-ink antialiased">
            <Header />
            <Marquee items={t.marquee} />
            <main>
              <AppRouter />
            </main>
            <Footer />
            <GlobalAmbience />
          </div>
          <Toaster position="bottom-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </LangContext.Provider>
  );
}

export default App;
